import { getEnv } from '@/shared/lib/env';

export const UI_LANGUAGE_KEY = 'ui_language';
export const PUBLIC_LOGIN_LANGUAGE_KEY = 'public_login_language';

export type UILanguage = 'ko' | 'en';

const PUBLIC_PATH_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password', '/manual'];

export function normalizeUILanguage(value: unknown): UILanguage | null {
  return value === 'ko' || value === 'en' ? value : null;
}

export function isPublicLanguagePath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function getStoredUILanguage(): UILanguage | null {
  try {
    return normalizeUILanguage(localStorage.getItem(UI_LANGUAGE_KEY));
  } catch {
    return null;
  }
}

export function setStoredUILanguage(lang: UILanguage) {
  try {
    localStorage.setItem(UI_LANGUAGE_KEY, lang);
  } catch {
    // ignore storage failures
  }
}

export function getBootLoginLanguage(): UILanguage | null {
  try {
    return normalizeUILanguage(sessionStorage.getItem(PUBLIC_LOGIN_LANGUAGE_KEY));
  } catch {
    return null;
  }
}

export function setBootLoginLanguage(lang: UILanguage) {
  try {
    sessionStorage.setItem(PUBLIC_LOGIN_LANGUAGE_KEY, lang);
  } catch {
    // ignore storage failures
  }
}

export function getPreferredAppLanguage(pathname: string = window.location.pathname): UILanguage {
  if (isPublicLanguagePath(pathname)) {
    return getBootLoginLanguage() ?? getStoredUILanguage() ?? 'ko';
  }

  return getStoredUILanguage() ?? 'ko';
}

export async function bootstrapPublicLoginLanguage(pathname: string = window.location.pathname): Promise<UILanguage | null> {
  if (!isPublicLanguagePath(pathname)) {
    return null;
  }

  try {
    const apiBaseUrl = getEnv('API_URL') || 'http://localhost:43011';
    const response = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/settings/feature`);
    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as { login_default_language?: unknown };
    const lang = normalizeUILanguage(data?.login_default_language);
    if (lang) {
      setBootLoginLanguage(lang);
      return lang;
    }
  } catch {
    // ignore bootstrap failures and fall back to local preferences
  }

  return null;
}
